"use client";

import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { handleFileUpload } from "@/components/fileCoverInput/inputMethods/fileUpload/handleFileUpload";
import { InvalidLengthMaxModal } from "@/components/fileCoverInput/inputMethods/InvalidLengthMaxModal";
import { InvalidLengthSubscriptionModal } from "@/components/InvalidLengthSubscriptionModal";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { SoftText } from "@/components/text/SoftText";
import { uploadAudioFileTypes } from "@/constants/audio";
import {
  MINUTES_MAX_UPLOAD_CONVERSION,
  MULTI_FILE_CLEAR_BUTTON_THRESHOLD,
} from "@/constants/constants";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { AudioInputStateFile } from "@/types/defs/audioInput";
import { AudioInputState, UserNoDates } from "@/types/types";
import { isAudioInputFile } from "@/utils/audioInput/is/isAudioInputFile";
import { cn } from "@/utils/cn";
import { formatDuration } from "@/utils/formatTime/formatDuration";

export function MultiFileCoverInput({
  audioInputs,
  hideClearButton,
  user,
  setInputFiles,
  currentAudioLengthS,
  clearInput,
  unset,
  submitting,
  maxNumberOfInputs,
  maxSingleAudioLengthMinutes = MINUTES_MAX_UPLOAD_CONVERSION,
  checkSubscription,
  shouldShowAudioLength,
  maxMultiAudioLengthMinutes,
  renderFilePresentation,
}: {
  audioInputs: AudioInputState[];
  hideClearButton: boolean;
  user: UserNoDates | null;
  setInputFiles: AudioInputState["setInputFile"][];
  currentAudioLengthS: number;
  clearInput: (index: number) => void;
  unset: () => void;
  submitting: boolean;
  maxNumberOfInputs: number;
  checkSubscription: boolean;
  maxSingleAudioLengthMinutes: number;
  shouldShowAudioLength: boolean;
  maxMultiAudioLengthMinutes: number | null;
  renderFilePresentation: (
    audioInput: AudioInputStateFile,
    index: number
  ) => ReactNode;
}) {
  const [
    invalidLengthSubscriptionModalOpen,
    setInvalidLengthSubscriptionModalOpen,
  ] = useState(false);
  const [invalidLengthMaxModalOpen, setInvalidLengthMaxModalOpen] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldShowTopFade, setShouldShowTopFade] = useState(false);

  const setAuthModalOpen = useSetAuthModalOpen();
  const audioInputsContainerRef = useRef<HTMLDivElement>(null);

  const audioInputsFile = audioInputs.filter((audioInput) =>
    isAudioInputFile(audioInput)
  );

  // Render in the order files were added (not by slot index) so a file added after deleting one stays below the rest instead of taking the freed slot.
  const orderedFileInputs = audioInputs
    .map((audioInput, index) => ({ audioInput, index }))
    .filter(
      (entry): entry is { audioInput: AudioInputStateFile; index: number } =>
        isAudioInputFile(entry.audioInput)
    )
    .sort(
      (first, second) =>
        first.audioInput.inputOrder - second.audioInput.inputOrder
    );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    multiple: true,
    useFsAccessApi: false,
    onDrop: async (acceptedFiles) => {
      // Any time a file is added here, the total number will be more than 1 so we might as well unset
      unset();

      await handleFileUpload({
        files: acceptedFiles,
        user,
        setAuthModalOpen,
        checkSubscription,
        setInvalidLengthSubscriptionModalOpen,
        audioLengthMinLimitMinutes: null,
        audioLengthMaxLimitMinutes: maxSingleAudioLengthMinutes,
        setInputFiles,
        currentAudioLengthS,
        setError,
        maxMultiAudioLengthMinutes,
      });
    },
    accept: uploadAudioFileTypes,
    noClick: audioInputsFile.length > 0,
  });

  const clearAudioInputs = () => {
    audioInputs.forEach((audioInput, index) => {
      if (isAudioInputFile(audioInput)) {
        clearInput(index);
      }
    });
    if (error !== null) {
      setError(null);
    }
  };

  useEffect(() => {
    if (audioInputsFile.length <= MULTI_FILE_CLEAR_BUTTON_THRESHOLD) {
      return;
    }

    const scrollContainer = audioInputsContainerRef.current;
    if (scrollContainer !== null && audioInputsFile.length > 0) {
      const hasScroll =
        scrollContainer.scrollHeight > scrollContainer.clientHeight;
      if (hasScroll) {
        // Check if user has reduced motion preference
        const hasReducedMotionPreference = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        requestAnimationFrame(() => {
          if (scrollContainer !== null) {
            scrollContainer.scrollTo({
              top: scrollContainer.scrollHeight,
              behavior: hasReducedMotionPreference ? "auto" : "smooth",
            });
          }
        });
      }
    }
  }, [audioInputsFile.length]);

  const handleScroll = () => {
    const scrollContainer = audioInputsContainerRef.current;
    if (scrollContainer !== null) {
      setShouldShowTopFade(scrollContainer.scrollTop > 0);
    }
  };

  return (
    <div className="relative">
      <InvalidLengthSubscriptionModal
        open={invalidLengthSubscriptionModalOpen}
        closeModal={() => setInvalidLengthSubscriptionModalOpen(false)}
        user={user}
      />

      <InvalidLengthMaxModal
        open={invalidLengthMaxModalOpen}
        closeModal={() => setInvalidLengthMaxModalOpen(false)}
        audioLengthMinLimitMinutes={null}
        audioLengthMaxLimitMinutes={maxSingleAudioLengthMinutes}
      />

      {audioInputsFile.length === 0 ?
        <div
          {...getRootProps({
            onClick: (event) => {
              if (user === null) {
                event.stopPropagation();
                setAuthModalOpen();
              }
            },
          })}
          className={cn(
            "relative flex cursor-pointer justify-center rounded-3xl border-2 border-dashed border-primary px-4 py-6 transition-colors",
            isDragActive ? "border-primaryLight bg-primaryLight" : "",
            error !== null ? "border-red-500" : "hover:border-primaryLight"
          )}
        >
          <div className="text-center">
            <ArrowUpTrayIcon
              className="mx-auto h-12 w-12 text-gray-300"
              aria-hidden="true"
            />
            <div className="mt-4 flex justify-center">
              <input {...getInputProps()} />
              <SmallTitle elementType="p" className="mb-1">
                Add or drop your audio files
              </SmallTitle>
            </div>
            <SoftText elementType="p">
              WAV, MP3, OGG, M4A, AIFF & FLAC.
            </SoftText>
            <SoftText elementType="p">
              {maxMultiAudioLengthMinutes} min max
            </SoftText>
          </div>
        </div>
      : <div
          {...getRootProps()}
          className="flex flex-col gap-2 rounded-3xl bg-white px-3 py-2 shadow-sm ring-1 ring-gray-900/5"
        >
          <div className="relative">
            {shouldShowTopFade && (
              <div className="pointer-events-none absolute inset-x-0 -top-[0.5] z-10 h-4 bg-gradient-to-b from-white via-white/90 to-transparent" />
            )}
            <div
              ref={audioInputsContainerRef}
              onScroll={handleScroll}
              className="flex max-h-[448px] flex-col gap-2 overflow-y-auto p-0.5 py-1"
            >
              {orderedFileInputs.map(({ audioInput, index }) =>
                renderFilePresentation(audioInput, index)
              )}
            </div>
          </div>

          <input {...getInputProps()} />

          <div
            className={cn(
              "flex gap-2 px-0.5",
              audioInputsFile.length > MULTI_FILE_CLEAR_BUTTON_THRESHOLD ?
                "mt-1"
              : ""
            )}
          >
            <button
              onClick={open}
              className={cn(
                "flex-1 cursor-pointer rounded-lg border py-0.5 text-center text-sm font-normal text-gray-900 shadow-sm ring-1 ring-gray-900/5 transition-colors hover:bg-primaryLight disabled:cursor-default disabled:bg-white disabled:text-gray-400",
                error !== null ? "border-red-500" : "border-transparent"
              )}
              disabled={
                audioInputsFile.length === maxNumberOfInputs || submitting
              }
            >
              + Add audio file
            </button>

            {!hideClearButton &&
              audioInputsFile.length > MULTI_FILE_CLEAR_BUTTON_THRESHOLD && (
                <button
                  onClick={clearAudioInputs}
                  disabled={submitting}
                  className="rounded-lg px-2 py-0.5 text-sm font-normal text-gray-900 shadow-sm ring-1 ring-gray-900/5 transition-colors hover:bg-primaryLight disabled:cursor-default disabled:bg-white disabled:text-gray-400"
                >
                  Clear all
                </button>
              )}
          </div>

          {shouldShowAudioLength && (
            <div className="mt-4">
              <div className="flex items-start justify-between px-0.5">
                <NormalText elementType="p" className="text-sm text-gray-500">
                  Duration
                </NormalText>
                <NormalText elementType="p" className="text-sm text-gray-500">
                  {formatDuration(currentAudioLengthS)}
                </NormalText>
              </div>
            </div>
          )}
        </div>
      }

      {error !== null && (
        <NormalText
          elementType="p"
          className="absolute -bottom-4 left-0 h-2 leading-4 text-red-500"
        >
          {error}
        </NormalText>
      )}
    </div>
  );
}
